import React, { ReactElement, useCallback, useMemo } from "react";

import { endOfDay } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import usePostHousehold from "@fartherfinance/frontend/api/Households/hooks/usePostHousehold";
import { CreateHouseholdRequestBody } from "@fartherfinance/frontend/api/Households/types";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import {
  HouseholdEstablishedAtFormField,
  HouseholdMemberFormField,
  HouseholdNameFormField,
} from "../HouseholdFormFields";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { EnhancedAutocompleteOption } from "@src/multiCustodian/pages/Advisor/common";
import { useClientsOptions } from "@src/multiCustodian/pages/Advisor/Tasks/hooks";
import { handleApiError } from "@src/utils/handleApiError";

import styles from "./CreateHouseholdsForm.module.css";

interface Form {
  householdName: string;
  ownerClientId: EnhancedAutocompleteOption<ClientId>;
  establishedAt?: Date | null;
}

interface Props {
  onCancel: () => void;
  onSuccess: () => void;
}

const formFieldRules = {
  ownerClientId: {
    required: "Owner client is required",
  },
};

const CreateHouseholdForm: React.FC<Props> = ({
  onCancel,
  onSuccess,
}: Props): ReactElement => {
  const { auth, advisorId } = useAdvisorPageInit();
  const notify = useStatusNotification();
  const { mutateAsync: postHousehold, isLoading } = usePostHousehold(auth);
  const { clientsOptions, clientsQuery } = useClientsOptions();

  const history = useHistory();
  const openHousehold = useCallback(
    (householdId) => {
      const location = history.location;
      history.push({
        ...location,
        pathname: `/Advisor/Households/${householdId}/Profile`,
      });
    },
    [history]
  );

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      householdName: "",
      ownerClientId: undefined,
      establishedAt: null,
    },
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const isDisabled = useMemo(
    () => isLoading || !isValid || !isDirty,
    [isLoading, isValid, isDirty]
  );

  const handleOnSave = async () => {
    if (!isValid || !advisorId) {
      return;
    }

    const values = getValues();

    try {
      const requestBody: CreateHouseholdRequestBody = {
        advisorId: advisorId,
        name: values.householdName,
        members: [
          {
            clientId: values.ownerClientId.value,
            memberType: "Owner",
          },
        ],
        ...(values.establishedAt && {
          establishedAt: endOfDay(values.establishedAt).toISOString(),
        }),
      };

      const response = await postHousehold(
        { body: requestBody },
        {
          onSuccess: () => {
            onSuccess();
            notify("Household created successfully", "Success");
          },
        }
      );

      openHousehold(response.data.householdId);
    } catch (error) {
      handleApiError({
        error,
        errorMessage: "Failed to create household",
        genericErrorMessage:
          "Something went wrong while creating the household. Please try again.",
        notify,
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleOnSave)}>
        <div className={styles.formField}>
          <HouseholdNameFormField />
        </div>
        <div className={styles.formField}>
          <HouseholdMemberFormField
            name="ownerClientId"
            label="Member"
            rules={formFieldRules.ownerClientId}
            clientsOptions={clientsOptions}
            clientsQuery={clientsQuery}
          />
        </div>
        <div className={styles.formField}>
          <HouseholdEstablishedAtFormField />
        </div>

        <div className={styles.footer}>
          <Button
            text="Cancel"
            onClick={onCancel}
            variant="outlined"
            buttonType="primary"
          />

          <Button
            text="Create Household"
            variant="contained"
            buttonType="primary"
            onClick={handleOnSave}
            disabled={isDisabled}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateHouseholdForm;
